import { API } from '../Auth';

const EP = 'discoverRuns';

// Platform-api routes the meta-analysis surface under /api/v1/profile/... (the same
// namespace as the start/results/cancel job endpoints). The c2-metadata-service is the
// worker behind it.
const OVERVIEW_PATH = '/api/v1/profile/meta-analysis/overview';
const JOB_RESULTS_PATH = (jobId) =>
  `/api/v1/profile/jobs/${encodeURIComponent(jobId)}/results`;
const JOB_MATCHES_PATH = (jobId) =>
  `/api/v1/profile/jobs/${encodeURIComponent(jobId)}/matches`;

// Maps the backend's engine / datasource_type tokens into the short codes the existing
// pill / colour system in constants.js already knows. Anything unknown falls through to
// 'file' so the card still renders something.
function shortType(engineOrType) {
  if (!engineOrType) return 'file';
  const t = String(engineOrType).toUpperCase();
  if (t === 'POSTGRESQL' || t === 'POSTGRES' || t === 'PG') return 'pg';
  if (t === 'MYSQL' || t === 'MARIADB') return 'mysql';
  if (t === 'MONGODB' || t === 'MONGO' || t === 'NOSQL') return 'mongo';
  if (t.startsWith('FILE_')) {
    if (t.includes('DELIMITED') || t.includes('CSV') || t.includes('TSV')) return 'csv';
    return 'file';
  }
  if (t === 'RELATIONAL') return 'pg';
  return 'file';
}

// Risk derivation from the commercial-match summary. With no commercial-match signal
// we report "unknown" — discovery hasn't told us what's in there yet. Only colour the
// card low/medium/high/critical when a commercial schema match actually backs it.
const REGULATORY_CRITICAL = new Set(['GDPR', 'PCI', 'PCI_DSS', 'HIPAA', 'SOX', 'CCPA']);

function deriveRisk(commercialMatch) {
  if (!commercialMatch) return 'unknown';
  const sens = (commercialMatch.defaultSensitivity || '').toUpperCase();
  const flags = (commercialMatch.regulatoryFlags || []).map((f) => String(f).toUpperCase());
  const isHighConfidence = (commercialMatch.confidence || 0) >= 0.7;
  if (isHighConfidence && flags.some((f) => REGULATORY_CRITICAL.has(f))) return 'critical';
  if (sens === 'HIGH') return 'high';
  if (sens === 'MEDIUM') return 'medium';
  if (sens === 'LOW') return 'low';
  return 'unknown';
}

function formatOrigin(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// Picks the representative member of a cluster for the card's primary label.
// Prefer the source whose dataSourceId matches the commercial match's source if
// one is annotated, otherwise the first member. Returns null for an empty cluster.
function pickPrimaryMember(raw) {
  const members = raw.dataSources || [];
  if (members.length === 0) return null;
  const cm = raw.commercialMatch;
  if (cm && cm.dataSourceId != null) {
    const match = members.find((m) => String(m.dataSourceId) === String(cm.dataSourceId));
    if (match) return match;
  }
  return members[0];
}

// Backend identity (post platform-api enrichment) → UI card shape. Existing identity
// rendering code in SchemaIdentityList / SchemaIdentityCard reads id / name / risk /
// instances / sizeGB / tables / rowsM / origin / types — match that contract.
function adaptIdentity(raw) {
  const types = (raw.engineTypes && raw.engineTypes.length > 0)
    ? raw.engineTypes.map(shortType)
    : (raw.dataSources || []).map((d) => shortType(d.engine || d.datasourceType));
  const sizeBytes = raw.totalSizeBytes || 0;
  const sizeGB = Math.round((sizeBytes / (1024 * 1024 * 1024)) * 100) / 100;
  const primary = pickPrimaryMember(raw);
  return {
    id: raw.id,
    name: raw.displayName || (raw.commercialMatch && raw.commercialMatch.productName) || 'Unnamed identity',
    // Card line 1 / line 2. primaryDatabase is the actual DB name on the target
    // server (populated by platform-api enrichment in PR-2); undefined hides line 2.
    primaryName: primary ? (primary.name || `Data source ${primary.dataSourceId}`) : null,
    primaryDatabase: primary ? (primary.database || null) : null,
    risk: deriveRisk(raw.commercialMatch),
    instances: raw.instanceCount,
    sizeGB,
    tables: raw.tableCount,
    rowsM: 0,  // backend doesn't carry row counts at the cluster level yet
    origin: formatOrigin(raw.earliestSnapshotAt),
    types,
    // Carry the enriched fields through so the detail view can render members
    // without an extra fetch.
    dataSources: raw.dataSources || [],
    commercialMatch: raw.commercialMatch || null,
    latestSnapshotAt: raw.latestSnapshotAt,
  };
}

export async function getOverview(minSimilarityPct) {
  const minSimilarity = minSimilarityPct != null
    ? Math.max(0.5, Math.min(1.0, minSimilarityPct / 100))
    : undefined;
  const params = minSimilarity != null
    ? { queryStringParameters: { minSimilarity } }
    : undefined;
  const response = await API.get(EP, OVERVIEW_PATH, params);
  // platform-api returns { generatedAt, totalDataSources, totalIdentities, identities }
  // — the UI components expect a flat array of identity cards.
  const identities = (response && response.identities) || [];
  return identities.map(adaptIdentity);
}

// Per-job results — used by future drill-down flows that already know the
// orchestration job id (not the identity id). Kept thin; the response shape is
// whatever c2-metadata-service's /api/v1/jobs/{runId}/results returns.
export async function getResults(jobId) {
  return API.get(EP, JOB_RESULTS_PATH(jobId));
}

// Commercial schema matches for one orchestration job.
export async function getMatches(jobId) {
  return API.get(EP, JOB_MATCHES_PATH(jobId));
}

const DATA_SOURCE_SUMMARY_PATH = (dataSourceId) =>
  `/api/v1/data-sources/${encodeURIComponent(dataSourceId)}/metadata/summary`;

// Per-data-source drilldown: latest snapshot's tables, users, connection facts,
// engine metadata. 404 if the source hasn't been profiled yet.
export async function getDataSourceSummary(dataSourceId) {
  return API.get(EP, DATA_SOURCE_SUMMARY_PATH(dataSourceId));
}
